<template>
  <fs-pill
    v-if="season || counter > 0"
    v-bind="$attrs"
    :color="season?.color"
    :counter="Boolean(counter)"
    :fixed="fixed"
    outlined
    :size="size"
    :tag="tag"
    :striped="striped"
    :hover-mode="hoverMode"
  >
    {{ season?.name ?? `+${counter}` }}
    <template #post>
      <slot name="post"></slot>
    </template>
  </fs-pill>
</template>

<script setup lang="ts" generic="yes">
// A pre-defined FsPill for season pills
//
// Pros:
// * Consistency - Having it pre-defined ensures consistency.
// * Easier to use - By having a much smaller interface/API than the
//   full FsPill component, it's faster to get a grip of all relevant
//   properties (by not having non-relevant props here).
// * Easier to refactor - Want to change all season pills? All code in
//   one place.

import FsPill from './fs-pill.vue';
import { useSeasons } from '@/store/seasons.ts';
import { computed } from 'vue';
import type { SeasonDto } from '@/ApiClient.ts';

export type FsPillSeasonProps = {
  fixed?: boolean;
  size?: 'tiny' | 'small' | 'medium' | 'large';
  tag?: string;
  /** Only some of the entities in scope use this season. */
  striped?: boolean;
  hoverMode?: 'add' | 'clear';
} & (
  | {
      season: SeasonDto;
      seasonId?: never;
      counter?: never;
    }
  | {
      season?: never;
      seasonId: number;
      counter?: never;
    }
  | {
      season?: never;
      seasonId?: never;
      counter: number;
    }
);

const props = withDefaults(defineProps<FsPillSeasonProps>(), {
  fixed: true,
  size: 'medium',
  tag: 'span',
});

const season = computed(() => props.season || (props.seasonId && useSeasons().byId[props.seasonId]));
</script>

<style scoped lang="scss">
@import '@styles/variables';

.pill--counter[data-specificity-hack] {
  background-color: $c-bg-light;
  border-color: $c-gray-mid-light;
  color: $c-dark;
}
</style>
